import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';

let appliedRequestSeq = 0;
let savedRequestSeq = 0;

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  loadingMore: false,
  savingJobId: null,
  jobList: [],
  totalJobs: null,
  individualJob: null,
  // jobId -> detail payload cache so re-opening a job renders instantly
  jobDetailsById: {},
  detailFetchingIds: {},
  // clientId -> hire-client details cache (about/founded/size/...)
  clientDetailsById: {},
  clientFetchingIds: {},
  error: null,
  appliedJobs: [],
  appliedTotal: 0,
  appliedLoading: false,
  savedJobs: [],
  savedTotal: 0,
  savedLoading: false,
  filterDropdown: {
    location: [],
    skills: [],
    company: [],
    role: []
  }
};

// remove duplicate jobs appended by infinite scroll
const appendUniqueJobs = (existingList, incomingJobs) => {
  const existingIds = new Set(existingList.map((job) => job._id));
  const freshJobs = incomingJobs.filter((job) => !existingIds.has(job._id));
  return [...existingList, ...freshJobs];
};

const slice = createSlice({
  name: 'opportunities',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    startLoadingMore(state) {
      state.loadingMore = true;
    },
    stopLoadingMore(state) {
      state.loadingMore = false;
    },
    matchedOpportunitiesSuccess(state, action) {
      state.isLoading = false;
      state.jobList = action.payload;
    },
    // first page of jobs — replaces the list
    filterOpportunitiesSuccess(state, action) {
      state.isLoading = false;
      state.loadingMore = false;
      state.jobList = action.payload.jobs;
      state.totalJobs = action.payload.totalJobs;
    },
    // subsequent pages — appended, de-duplicated
    getPageSuccess(state, action) {
      state.isLoading = false;
      state.loadingMore = false;
      state.jobList = appendUniqueJobs(state.jobList, action.payload.jobs);
      state.totalJobs = action.payload.totalJobs;
    },
    // merge backend-provided or optimistic flags into a single job in the list
    updateJobFlags(state, action) {
      const { jobId, patch } = action.payload;
      const job = state.jobList.find((j) => j._id === jobId);
      if (job) {
        Object.assign(job, patch);
      }
      // keep the detail cache in sync so the right panel stays live
      if (state.jobDetailsById[jobId]) {
        Object.assign(state.jobDetailsById[jobId], patch);
      }
    },
    // detail cache
    setJobDetail(state, action) {
      const { jobId, job } = action.payload;
      state.jobDetailsById[jobId] = job;
    },
    detailFetchingStart(state, action) {
      state.detailFetchingIds[action.payload] = true;
    },
    detailFetchingEnd(state, action) {
      delete state.detailFetchingIds[action.payload];
    },
    // client details cache (hire-backend, per clientId)
    setClientDetails(state, action) {
      const { clientId, client } = action.payload;
      state.clientDetailsById[clientId] = client;
    },
    clientFetchingStart(state, action) {
      state.clientFetchingIds[action.payload] = true;
    },
    clientFetchingEnd(state, action) {
      delete state.clientFetchingIds[action.payload];
    },
    // optimistic toggle before the save request resolves
    toggleSavedJob(state, action) {
      const jobId = action.payload;
      const job = state.jobList.find((j) => j._id === jobId);
      if (job && job.isSaved !== undefined) {
        state.jobList = state.jobList.map((j) =>
          j._id === jobId ? { ...j, isSaved: !j.isSaved } : j
        );
      }
      const cached = state.jobDetailsById[jobId];
      if (cached && cached.isSaved !== undefined) {
        cached.isSaved = !cached.isSaved;
      }
    },
    applyJobSuccess(state, action) {
      const jobId = action.payload;
      state.jobList = state.jobList.map((j) =>
        j._id === jobId ? { ...j, isCandidateApplied: true } : j
      );
      state.savedJobs = state.savedJobs.map((j) =>
        j._id === jobId ? { ...j, isCandidateApplied: true } : j
      );
      if (state.jobDetailsById[jobId]) {
        state.jobDetailsById[jobId].isCandidateApplied = true;
      }
      let appliedJob = state.jobList.find((job) => job._id === jobId);
      state.isLoading = false;
      if (appliedJob && !state.appliedJobs.some((job) => job._id === jobId)) {
        state.appliedJobs = [...state.appliedJobs, appliedJob];
        state.appliedTotal = state.appliedTotal + 1;
      }
    },
    appliedLoadingStart(state) {
      state.appliedLoading = true;
    },
    appliedJobsSuccess(state, action) {
      state.appliedLoading = false;
      state.appliedJobs = action.payload.appliedJobs;
      state.appliedTotal = action.payload.totalJobs;
    },
    appendAppliedJobs(state, action) {
      state.appliedLoading = false;
      state.appliedJobs = appendUniqueJobs(
        state.appliedJobs,
        action.payload.appliedJobs
      );
      state.appliedTotal = action.payload.totalJobs;
    },
    insertAppliedJob(state, action) {
      const job = action.payload;
      if (job && !state.appliedJobs.some((j) => j._id === job._id)) {
        state.appliedJobs = [job, ...state.appliedJobs];
        state.appliedTotal = state.appliedTotal + 1;
      }
    },
    removeAppliedJob(state, action) {
      const jobId = action.payload;
      if (state.appliedJobs.some((job) => job._id === jobId)) {
        state.appliedJobs = state.appliedJobs.filter((job) => job._id !== jobId);
        state.appliedTotal = Math.max(0, state.appliedTotal - 1);
      }
    },
    savedLoadingStart(state) {
      state.savedLoading = true;
    },
    savedJobsSuccess(state, action) {
      state.savedLoading = false;
      state.savedJobs = action.payload.savedJobs;
      state.savedTotal = action.payload.totalJobs;
    },
    appendSavedJobs(state, action) {
      state.savedLoading = false;
      state.savedJobs = appendUniqueJobs(
        state.savedJobs,
        action.payload.savedJobs
      );
      state.savedTotal = action.payload.totalJobs;
    },
    insertSavedJob(state, action) {
      const job = action.payload;
      if (job && !state.savedJobs.some((j) => j._id === job._id)) {
        state.savedJobs = [job, ...state.savedJobs];
        state.savedTotal = state.savedTotal + 1;
      }
    },
    removeSavedJob(state, action) {
      const jobId = action.payload;
      if (state.savedJobs.some((job) => job._id === jobId)) {
        state.savedJobs = state.savedJobs.filter((job) => job._id !== jobId);
        state.savedTotal = Math.max(0, state.savedTotal - 1);
      }
    },
    hasError(state, action) {
      state.isLoading = false;
      state.loadingMore = false;
      state.appliedLoading = false;
      state.savedLoading = false;
      state.error = action.payload;
    },
    setJobId(state, action) {
      state.job_id = action.payload;
    },
    filterOptions(state, action) {
      state.filterDropdown.role = action.payload.map((role) => ({
        key: role,
        value: role,
        label: role
      }));
    }
  }
});

export const { setJobId, updateJobFlags } = slice.actions;

// Reducer
export default slice.reducer;
// ----------------------------------------------------------------------

export function getMatchedopportunities(talentCard) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(
        'https://matching.qrata.ai/match',
        talentCard
      );
      dispatch(
        slice.actions.matchedOpportunitiesSuccess(
          response.data['matched candidate']
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function getFilteropportunities(filter, talentId) {
  const page = filter.page || 1;

  return async (dispatch) => {
    if (page > 1) {
      dispatch(slice.actions.startLoadingMore());
    } else {
      dispatch(slice.actions.startLoading());
    }

    try {
      const response = await axios.post('/api/talent/jobs/filter', filter);
      const { jobs, totalJobs } = response.data;

      if (page > 1) {
        dispatch(slice.actions.getPageSuccess({ jobs, totalJobs }));
      } else {
        dispatch(slice.actions.filterOpportunitiesSuccess({ jobs, totalJobs }));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.stopLoadingMore());
    }
  };
}

// Fetch (and cache) a single job's detail. Cached jobs are served instantly by
// the caller — the request still runs as a silent background revalidation so
// server-side view marking and fresh flags are picked up.
export function getJobDetail(jobId) {
  return async (dispatch, getState) => {
    const state = getState().opportunities;
    if (state.detailFetchingIds[jobId]) return undefined;

    dispatch(slice.actions.detailFetchingStart(jobId));
    try {
      const response = await axios.get(`/api/talent/jobs/${jobId}`);
      const jobData = {
        ...response.data.job,
        isCandidateApplied: response.data.isCandidateApplied,
        isSaved: response.data.isSaved,
        isViewed: response.data.isViewed
      };
      dispatch(slice.actions.setJobDetail({ jobId, job: jobData }));
      dispatch(
        slice.actions.updateJobFlags({
          jobId,
          patch: {
            isViewed: jobData.isViewed,
            isSaved: jobData.isSaved,
            isCandidateApplied: jobData.isCandidateApplied
          }
        })
      );
      return jobData;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return undefined;
    } finally {
      dispatch(slice.actions.detailFetchingEnd(jobId));
    }
  };
}

// Client details from the hire backend (about/founded/size/funding/...),
// cached per clientId. Call freely on every job change — cached clients are
// served instantly, in-flight requests are deduped.
export function getClientDetails(clientId) {
  return async (dispatch, getState) => {
    if (!clientId) return undefined;
    const state = getState().opportunities;
    if (state.clientDetailsById[clientId] || state.clientFetchingIds[clientId])
      return undefined;

    dispatch(slice.actions.clientFetchingStart(clientId));
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_HIRE_BACKEND}/api/client/getClient/${clientId}`
      );
      // the hire API may respond with an array or a single object
      const client = Array.isArray(response.data)
        ? response.data[0]
        : response.data;
      dispatch(slice.actions.setClientDetails({ clientId, client }));
      return client;
    } catch (error) {
      console.error('Error fetching client details:', error);
      return undefined;
    } finally {
      dispatch(slice.actions.clientFetchingEnd(clientId));
    }
  };
}

// Optimistic save toggle: flip the bookmark instantly, reconcile with the
// server response, roll back if the request fails. Never touches global
// isLoading so the list doesn't flicker.
export function saveJob(jobId) {
  return async (dispatch) => {
    dispatch(slice.actions.toggleSavedJob(jobId));
    try {
      const response = await axios.post(`/api/talent/jobs/save/${jobId}`);
      dispatch(
        slice.actions.updateJobFlags({
          jobId,
          patch: { isSaved: response.data.isSaved }
        })
      );
    } catch (error) {
      dispatch(slice.actions.toggleSavedJob(jobId)); // rollback
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function filterDropdownOptions() {
  return async (dispatch) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_HIRE_BACKEND}/api/options/role`
      );

      let allSubRoleData = [];
      response.data.map((role) => {
        allSubRoleData = [...allSubRoleData, ...role.subCategories];
      });

      dispatch(slice.actions.filterOptions(allSubRoleData));
    } catch (err) {
    }
  };
}
// ----------------------------------------------------------------------

export function applyJob(job_id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.setJobId(job_id));
    try {
      await axios.post(`/api/talent/jobs/apply/${job_id}`);
      dispatch(slice.actions.applyJobSuccess(job_id));
      return true;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}

export function getMyApplications(filters = {}) {
  const page = filters.page || 1;

  return async (dispatch) => {
    const requestSeq = ++appliedRequestSeq;
    dispatch(slice.actions.appliedLoadingStart());
    try {
      const response = await axios.get(`/api/talent/jobs/applied-jobs`, {
        params: { ...filters, page }
      });
      if (requestSeq !== appliedRequestSeq) return;

      const { appliedJobs, totalJobs } = response.data;

      if (page > 1) {
        dispatch(slice.actions.appendAppliedJobs({ appliedJobs, totalJobs }));
      } else {
        dispatch(slice.actions.appliedJobsSuccess({ appliedJobs, totalJobs }));
      }
    } catch (error) {
      if (requestSeq !== appliedRequestSeq) return;
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSavedJobs(filters = {}) {
  const page = filters.page || 1;

  return async (dispatch) => {
    const requestSeq = ++savedRequestSeq;
    dispatch(slice.actions.savedLoadingStart());
    try {
      const response = await axios.get(`/api/talent/jobs/saved`, {
        params: { ...filters, page }
      });
      if (requestSeq !== savedRequestSeq) return;

      const { savedJobs, totalJobs } = response.data;

      if (page > 1) {
        dispatch(slice.actions.appendSavedJobs({ savedJobs, totalJobs }));
      } else {
        dispatch(slice.actions.savedJobsSuccess({ savedJobs, totalJobs }));
      }
    } catch (error) {
      if (requestSeq !== savedRequestSeq) return;
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function withdrawJob(jobId) {
  return async (dispatch, getState) => {
    const removed = getState().opportunities.appliedJobs.find(
      (job) => job._id === jobId
    );
    dispatch(slice.actions.removeAppliedJob(jobId));
    try {
      await axios.post(`/api/talent/jobs/withdraw/${jobId}`);
      return true;
    } catch (error) {
      if (removed) dispatch(slice.actions.insertAppliedJob(removed));
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}

export function unsaveJob(jobId) {
  return async (dispatch, getState) => {
    const removed = getState().opportunities.savedJobs.find(
      (job) => job._id === jobId
    );
    dispatch(slice.actions.removeSavedJob(jobId));
    dispatch(slice.actions.updateJobFlags({ jobId, patch: { isSaved: false } }));
    try {
      await axios.post(`/api/talent/jobs/save/${jobId}`);
    } catch (error) {
      if (removed) dispatch(slice.actions.insertSavedJob(removed));
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------
